import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { CarAvailabilityBadge } from "@/lib/carStatus";
import ExchangeRateDialog from "@/components/ExchangeRateDialog";
import { LayoutDashboard, Car, Users as UsersIcon, Receipt, CalendarDays, LogOut, Search, Plus, X, Trophy, KeyRound, ChevronDown, Shield, Download, Upload, ListChecks, DatabaseBackup, Wallet, Euro } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api } from "@/lib/api";
import QuickAddDialog from "@/components/QuickAddDialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import ImportDataDialog from "@/components/ImportDataDialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const NAV_ALL = [
  { to: "/", label: "Dashboard", short: "Panou", icon: LayoutDashboard, end: true, adminOnly: true },
  { to: "/cars", label: "Mașini", icon: Car },
  { to: "/clients", label: "Clienți", icon: UsersIcon },
  { to: "/expenses", label: "Costuri", icon: Receipt, adminOnly: true },
  { to: "/sales", label: "Vânzări", icon: Trophy },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/todos", label: "Task-uri", icon: ListChecks },
  { to: "/employee-expenses", label: "Chelt. angajați", short: "Angajați", icon: Wallet, adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { rateMeta, rate } = useCurrency();
  const isAdmin = user?.role === "admin";
  const NAV = NAV_ALL.filter((n) => !n.adminOnly || isAdmin);
  const [fabOpen, setFabOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const isManualRate = rateMeta?.source === "Manual";
  const rateChip = rateMeta?.source && (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${isManualRate ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"}`} data-testid="rate-source-badge">
      {isManualRate ? "Manual" : "BNR"}
    </span>
  );
  const [quickAdd, setQuickAdd] = useState(null); // 'car' | 'client' | 'expense' | 'interaction'
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");

  async function downloadExport(kind) {
    try {
      const res = await api.get(`/admin/export.${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `novoauto-backup-${stamp}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      // toast?
    }
  }
  const [results, setResults] = useState(null);
  const navigate = useNavigate();
  const debounceRef = useRef();

  useEffect(() => {
    if (!search || search.trim().length < 2) {
      setResults(null);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q: search.trim() } });
        setResults(data);
      } catch (e) {
        setResults(null);
      }
    }, 250);
  }, [search]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Topbar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2 shrink-0" data-testid="app-logo">
            <img src="/logo.png" alt="Novo Auto" className="h-10 w-auto" />
          </div>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="global-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută VIN, marcă, telefon..."
              className="w-full bg-slate-100 border border-transparent rounded-full py-2.5 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF] focus:bg-white"
            />
            {results && (results.cars.length || results.clients.length) ? (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-50 max-h-96 overflow-y-auto">
                {results.cars.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 bg-slate-50">Mașini</div>
                    {results.cars.map((c) => (
                      <button
                        data-testid={`search-result-car-${c.id}`}
                        key={c.id}
                        onClick={() => { setSearch(""); setResults(null); navigate(`/cars/${c.id}`); }}
                        className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 flex justify-between items-center gap-2 ${c.status === "vanduta" ? "opacity-55" : ""}`}
                      >
                        <span className="text-sm min-w-0 truncate">{c.make} {c.model} <span className="text-slate-400">· {c.year || "—"}</span></span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-400 font-mono">{c.vin?.slice(-6)}</span>
                          <CarAvailabilityBadge status={c.status} testid={`search-result-car-status-${c.id}`} />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {results.clients.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 bg-slate-50">Clienți</div>
                    {results.clients.map((c) => (
                      <button
                        data-testid={`search-result-client-${c.id}`}
                        key={c.id}
                        onClick={() => { setSearch(""); setResults(null); navigate(`/clients/${c.id}`); }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 flex justify-between items-center"
                      >
                        <span className="text-sm">{c.name}</span>
                        <span className="text-xs text-slate-400">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
          {rateMeta?.source && (
            <button
              type="button"
              onClick={() => isAdmin && setRateOpen(true)}
              data-testid="topbar-rate-chip"
              className={`hidden sm:inline-flex items-center gap-1.5 shrink-0 text-xs tabular px-2.5 py-1.5 rounded-full border border-slate-200 bg-white transition-colors ${isAdmin ? "hover:border-slate-400 cursor-pointer" : "cursor-default"}`}
            >
              1 € = {rate?.toFixed(4).replace(".", ",")} lei {rateChip}
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors px-1 py-1 pr-2.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${isAdmin ? "bg-[#0A0F1C] text-white" : "bg-white text-slate-700 border border-slate-200"}`}>
                  {(user?.name || user?.email || "?").substring(0, 1).toUpperCase()}
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-content">
              <DropdownMenuLabel>
                <div className="text-xs uppercase tracking-widest text-slate-400">{isAdmin ? "Administrator" : "Limited (sales)"}</div>
                <div className="text-sm font-medium truncate">{user?.email}</div>
                {rateMeta?.source && (
                  <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1.5" data-testid="menu-rate-info">1 € = {rate?.toFixed(4).replace(".", ",")} lei {rateChip}</div>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setChangePwdOpen(true)} data-testid="menu-change-password">
                <KeyRound className="w-4 h-4 mr-2" /> Schimbă parola
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/users")} data-testid="menu-users">
                  <Shield className="w-4 h-4 mr-2" /> Utilizatori
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => setRateOpen(true)} data-testid="menu-exchange-rate">
                  <Euro className="w-4 h-4 mr-2" /> Curs valutar
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/backups")} data-testid="menu-backups">
                  <DatabaseBackup className="w-4 h-4 mr-2" /> Backup-uri
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => downloadExport("xlsx")} data-testid="menu-export-xlsx">
                  <Download className="w-4 h-4 mr-2" /> Export Excel (backup)
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => downloadExport("json")} data-testid="menu-export-json">
                  <Download className="w-4 h-4 mr-2" /> Export JSON (backup)
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => setImportOpen(true)} data-testid="menu-import-json">
                  <Upload className="w-4 h-4 mr-2" /> Import date (restore)
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-600 focus:text-red-700">
                <LogOut className="w-4 h-4 mr-2" /> Ieșire
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto max-w-6xl px-4 pt-4 pb-32">
        <Outlet />
      </main>

      {/* FAB */}
      <div className="fixed right-4 bottom-24 sm:bottom-8 z-50 flex flex-col items-end gap-2">
        {fabOpen && (
          <div className="flex flex-col gap-2 mb-2 fade-up">
            {[
              { k: "car", label: "Mașină nouă" },
              { k: "client", label: "Client nou" },
              { k: "expense", label: "Cheltuială" },
              { k: "interaction", label: "Interacțiune" },
              { k: "_event", label: "Eveniment calendar" },
            ].map((item) => (
              <button
                key={item.k}
                data-testid={`fab-add-${item.k}`}
                onClick={() => {
                  setFabOpen(false);
                  if (item.k === "_event") {
                    navigate("/calendar?new=1");
                  } else {
                    setQuickAdd(item.k);
                  }
                }}
                className="bg-white border border-slate-200 shadow-md rounded-full px-4 py-2 text-sm hover:border-slate-400 transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
        <button
          data-testid="fab-toggle"
          onClick={() => setFabOpen((o) => !o)}
          className="w-14 h-14 rounded-full bg-[#0047FF] text-white shadow-xl hover:bg-[#003BD3] flex items-center justify-center transition-all"
          style={{ transform: fabOpen ? "rotate(45deg)" : "rotate(0)" }}
          aria-label="Adaugă"
        >
          {fabOpen ? <X className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
        </button>
      </div>

      {isAdmin && <ExchangeRateDialog open={rateOpen} onOpenChange={setRateOpen} />}

      {/* Bottom nav (mobile) */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-slate-200 pb-safe">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${NAV.length}, minmax(0, 1fr))` }}>
          {NAV.map((n) => (
            <NavLink
              data-testid={`bottom-nav-${n.to.replace("/", "") || "home"}`}
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2 text-[10px] min-w-0 ${isActive ? "text-[#0047FF]" : "text-slate-500"}`
              }
            >
              <n.icon className="w-5 h-5" />
              <span className="truncate max-w-full px-0.5">{n.short || n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Desktop nav */}
      <nav className="hidden sm:block fixed left-4 top-20 w-44">
        <div className="bg-white border border-slate-200 rounded-2xl p-2 space-y-1">
          {NAV.map((n) => (
            <NavLink
              data-testid={`side-nav-${n.to.replace("/", "") || "home"}`}
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-[#0A0F1C] text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <QuickAddDialog kind={quickAdd} onClose={() => setQuickAdd(null)} />
      <ChangePasswordDialog open={changePwdOpen} onClose={() => setChangePwdOpen(false)} />
      <ImportDataDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={() => {
        // soft reload after a successful import so all pages re-fetch fresh data
        setTimeout(() => window.location.reload(), 800);
      }} />
    </div>
  );
}
